import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  Chip,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Tabs,
  Tab,
  Snackbar
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Clear as ClearIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  OpenInFull as OpenInFullIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  School as AcademyIcon,
  SupervisorAccount as ManagerIcon,
  Badge as HrIcon,
  Sync as SyncIcon,
  RateReview as ReviewIcon,
  FolderSpecial as ProjectIcon
} from '@mui/icons-material';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import EditorConvertToHTML from '../utils/TextEditor';
import {
  getAllProbationReviews,
  updateProbationReview,
  backfillProbationReviews
} from '../services/probationReviewAPIs';
import { getEmployeeImage } from '../services/HRstaff';
import { getCandidateImage } from '../services/onboardingapi';
import { IMAGE_BASE_URL } from '../config';
import marabesLogo from '../assets/images/logos/Marabes.png';
import axopeopleLogo from '../assets/images/logos/Axopeople.png';
import { RestrictedToRole } from '../roles/RestrictedToRole';

if (typeof jsPDF.prototype.autoTable !== 'function') {
  jsPDF.prototype.autoTable = autoTable;
}

// === THEME PALETTE ===
const THEME = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  primary: '#1E293B',
  secondary: '#64748B',
  accent: '#3B82F6',
  accentDark: '#1D4ED8',
  accentLight: '#EFF6FF',
  border: '#E2E8F0',
  success: '#10B981',
  successLight: '#ECFDF5',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  purple: '#8B5CF6',
  purpleLight: '#F5F3FF',
  teal: '#0D9488',
  tealLight: '#F0FDFA',
  rose: '#E11D48',
  roseLight: '#FFF1F2',
  tableHeader: '#F1F5F9'
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: { xs: '16px', md: '24px' },
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  logoBanner: {
    backgroundColor: '#F3F4F6',
    borderRadius: '14px',
    border: `1px solid ${THEME.border}`,
    px: { xs: 2, md: 3 },
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  },
  logoBannerTitle: {
    fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.3rem' },
    fontWeight: '800',
    color: '#0F2C59',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    flexGrow: 1
  },
  headerCard: {
    backgroundColor: THEME.surface,
    padding: { xs: '20px', md: '24px' },
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04), 0 2px 4px -1px rgba(0,0,0,0.02)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${THEME.border}`,
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: '800',
    color: THEME.primary,
    letterSpacing: '-0.025em',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  subTitle: {
    fontSize: '0.875rem',
    color: THEME.secondary,
    marginTop: '4px'
  },
  statCard: {
    backgroundColor: THEME.surface,
    padding: '20px',
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.06)'
    }
  },
  iconBox: (bg, color) => ({
    width: 48,
    height: 48,
    borderRadius: '12px',
    backgroundColor: bg,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  filterCard: {
    backgroundColor: THEME.surface,
    padding: '16px 20px',
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  },
  tableCard: {
    backgroundColor: THEME.surface,
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)',
    overflow: 'hidden'
  },
  th: {
    backgroundColor: THEME.tableHeader,
    color: THEME.primary,
    fontWeight: '700',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '14px 16px',
    borderBottom: `2px solid ${THEME.border}`,
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '12px 16px',
    color: '#334155',
    fontSize: '0.875rem',
    borderBottom: `1px solid ${THEME.border}`
  },
  reviewCard: {
    backgroundColor: THEME.surface,
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
    transition: 'all 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
      borderColor: THEME.accent
    }
  },
  richHtmlContent: {
    fontSize: '0.9rem',
    lineHeight: 1.65,
    color: '#334155',
    '& p': { margin: '0 0 10px 0' },
    '& p:last-child': { margin: 0 },
    '& ul, & ol': { margin: '4px 0 10px 20px', paddingLeft: '8px' },
    '& li': { margin: '3px 0' },
    '& b, & strong': { fontWeight: 700, color: '#1E293B' },
    '& em, & i': { fontStyle: 'italic' },
    '& h1, & h2, & h3, & h4': { margin: '12px 0 6px 0', color: '#0F172A', fontWeight: 700 },
    '& br': { display: 'block', content: '""', marginTop: '4px' }
  }
};

// === HTML & ASSET HELPERS ===
const isHtml = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);

const stripHtml = (html) => {
  if (!html) return '';
  if (typeof html !== 'string') return String(html);
  const processed = html
    .replace(/<br\s*[\/]?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<\/li>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  const tmp = document.createElement('div');
  tmp.innerHTML = processed;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const getBase64FromUrl = async (url) => {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width || 64;
            canvas.height = img.naturalHeight || img.height || 64;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (e) {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = url;
      } catch (err) {
        resolve(null);
      }
    });
  }
};

// Date Parsing Helper
const parseDate = (dateVal) => {
  if (!dateVal) return null;
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return new Date(year, month - 1, day);
  }
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateVal)) {
    const [year, month, day] = dateVal.substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(dateVal);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Date Formatter Helper (DD/MM/YYYY)
const formatDisplayDate = (dateVal) => {
  const d = parseDate(dateVal);
  if (!d) return 'N/A';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Avatar Color Generator
const getAvatarColor = (name) => {
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1', '#14B8A6'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  if (!name) return 'E';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const makeCircularImageBase64 = (imgSource) => {
  if (!imgSource) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const size = Math.min(w, h);
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(imgSource);

        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        const sx = (w - size) / 2;
        const sy = (h - size) / 2;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);

        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(imgSource);
      }
    };
    img.onerror = () => resolve(imgSource);
    img.src = imgSource;
  });
};

const createInitialsAvatarBase64 = (name) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = getAvatarColor(name);
    ctx.beginPath();
    ctx.arc(32, 32, 30, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(getInitials(name), 32, 33);

    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
};

/**
 * Dedicated Circular Avatar Component
 */
const EmployeeAvatar = ({ employeeId, name, size = 40, fontSize = '0.875rem', cachedImages = {} }) => {
  const [imgSrc, setImgSrc] = useState(cachedImages[employeeId] || null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (cachedImages[employeeId]) {
      setImgSrc(cachedImages[employeeId]);
      return;
    }

    let isMounted = true;
    const load = async () => {
      if (!employeeId) return;
      try {
        const url = await getEmployeeImage(employeeId);
        if (url && isMounted) {
          setImgSrc(url);
        }
      } catch (e) {
        // Fallback
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [employeeId, cachedImages]);

  const initials = getInitials(name);
  const bgColor = getAvatarColor(name);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: bgColor,
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: fontSize,
        overflow: 'hidden',
        flexShrink: 0,
        border: '2px solid #FFFFFF',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        position: 'relative'
      }}
    >
      {imgSrc && !imgError ? (
        <Box
          component="img"
          src={imgSrc}
          alt={name}
          onError={() => setImgError(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
          }}
        />
      ) : (
        initials
      )}
    </Box>
  );
};

/**
 * Editable Feedback Cell Component with Quick Edit Icon and Preview
 */
const EditableFeedbackCell = ({
  value,
  fieldKey,
  fieldLabel,
  review,
  icon: IconComponent,
  iconColor,
  onOpenEdit,
  onOpenView
}) => {
  const hasContent = value && typeof value === 'string' && value.trim().length > 0 && stripHtml(value).length > 0;
  const plainText = stripHtml(value || '');

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: '10px',
        border: `1px solid ${hasContent ? THEME.border : '#F1F5F9'}`,
        backgroundColor: hasContent ? '#FAFCFF' : '#F8FAFC',
        transition: 'all 0.2s ease',
        position: 'relative',
        minHeight: '62px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
          borderColor: THEME.accent,
          boxShadow: '0 2px 6px rgba(59, 130, 246, 0.08)',
          '& .action-btns': { opacity: 1 }
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        {IconComponent && (
          <Box sx={{ color: iconColor || THEME.accent, mt: 0.2, flexShrink: 0 }}>
            <IconComponent sx={{ fontSize: '1rem' }} />
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {hasContent ? (
            <Typography
              onClick={() => onOpenView({ title: `${fieldLabel} - ${review.employeeName}`, content: value, icon: IconComponent, iconColor })}
              sx={{
                fontSize: '0.825rem',
                color: '#334155',
                lineHeight: 1.45,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
                '&:hover': { color: THEME.accentDark, textDecoration: 'underline' }
              }}
            >
              {plainText}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontStyle: 'italic' }}>
              None
            </Typography>
          )}
        </Box>
      </Box>

      {/* Action Buttons Bar */}
      <Stack
        className="action-btns"
        direction="row"
        spacing={0.5}
        justifyContent="flex-end"
        alignItems="center"
        sx={{
          mt: 0.5,
          opacity: { xs: 1, sm: 0.85 },
          transition: 'opacity 0.2s ease'
        }}
      >
        {hasContent && (
          <Tooltip title="Expand & View Full Content">
            <IconButton
              size="small"
              onClick={() => onOpenView({ title: `${fieldLabel} - ${review.employeeName}`, content: value, icon: IconComponent, iconColor })}
              sx={{ p: 0.4, color: THEME.secondary, '&:hover': { color: THEME.accent } }}
            >
              <OpenInFullIcon sx={{ fontSize: '0.85rem' }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={hasContent ? `Edit ${fieldLabel}` : `Add ${fieldLabel}`}>
          <Button
            size="small"
            variant="text"
            startIcon={<EditIcon sx={{ fontSize: '0.8rem !important' }} />}
            onClick={() => onOpenEdit(review, fieldKey, fieldLabel)}
            sx={{
              p: '2px 6px',
              minWidth: 0,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              color: hasContent ? THEME.accent : THEME.secondary,
              backgroundColor: hasContent ? THEME.accentLight : 'transparent',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: THEME.accentLight,
                color: THEME.accentDark
              }
            }}
          >
            {hasContent ? 'Edit' : 'Add'}
          </Button>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const ProbationReviewReport = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [cachedImages, setCachedImages] = useState({});

  // View mode: 'table' or 'grid'
  const [viewMode, setViewMode] = useState('table');

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('ALL');
  const [selectedContractType, setSelectedContractType] = useState('ALL');
  const [selectedFeedbackStatus, setSelectedFeedbackStatus] = useState('ALL');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Pagination & Sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('contractStartDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal States
  const [selectedReview, setSelectedReview] = useState(null);
  const [dossierOpen, setDossierOpen] = useState(false);

  // Single / Full Feedback Edit Dialog State
  const [editModal, setEditModal] = useState({
    open: false,
    review: null,
    activeTab: 'managerFeedback', // 'managerFeedback' | 'hrFeedback' | 'academyFeedback' | 'all'
    managerFeedback: '',
    hrFeedback: '',
    academyFeedback: '',
    singleFieldMode: false,
    fieldLabel: ''
  });
  const [savingFeedback, setSavingFeedback] = useState(false);

  // View HTML Dialog State
  const [htmlDialog, setHtmlDialog] = useState({
    open: false,
    title: '',
    content: '',
    icon: null,
    iconColor: null
  });

  // Notification Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Fetch all probation reviews
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllProbationReviews();
      const list = Array.isArray(data) ? data : [];
      setReviews(list);
    } catch (err) {
      console.error('Error loading probation reviews:', err);
      setError(err.message || 'Failed to fetch probation reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Asynchronously pre-fetch employee images
  useEffect(() => {
    let isMounted = true;
    const loadImages = async () => {
      const newImages = {};
      for (const item of reviews) {
        if (item.employeeId && !cachedImages[item.employeeId]) {
          try {
            const url = await getEmployeeImage(item.employeeId);
            if (url && isMounted) {
              newImages[item.employeeId] = url;
            }
          } catch (e) {
            // Ignore
          }
        }
      }
      if (isMounted && Object.keys(newImages).length > 0) {
        setCachedImages((prev) => ({ ...prev, ...newImages }));
      }
    };

    if (reviews && reviews.length > 0) {
      loadImages();
    }

    return () => {
      isMounted = false;
    };
  }, [reviews]);

  // Backfill / Sync Eligible Contracts
  const handleBackfill = async () => {
    setSyncing(true);
    try {
      const res = await backfillProbationReviews();
      const createdCount = res?.createdCount || res?.created?.length || 0;
      const alreadyInit = res?.alreadyInitializedCount || 0;
      setToast({
        open: true,
        message: `Sync complete: ${createdCount} new reviews created (${alreadyInit} already initialized).`,
        severity: 'success'
      });
      await fetchData();
    } catch (err) {
      console.error('Backfill error:', err);
      setToast({
        open: true,
        message: `Sync failed: ${err.message || 'Could not backfill eligible contracts'}`,
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  };

  // Unique departments from data
  const departments = useMemo(() => {
    const depSet = new Set();
    reviews.forEach((item) => {
      if (item.department && item.department.trim()) {
        depSet.add(item.department.trim());
      }
    });
    return Array.from(depSet).sort();
  }, [reviews]);

  // Unique contract types from data
  const contractTypes = useMemo(() => {
    const ctSet = new Set();
    reviews.forEach((item) => {
      if (item.contractType && item.contractType.trim()) {
        ctSet.add(item.contractType.trim());
      }
    });
    return Array.from(ctSet).sort();
  }, [reviews]);

  // Filtered and Sorted Reviews
  const filteredData = useMemo(() => {
    return reviews
      .filter((item) => {
        // Department Filter
        if (selectedDepartment !== 'ALL' && item.department !== selectedDepartment) {
          return false;
        }

        // Contract Type Filter
        if (selectedContractType !== 'ALL' && item.contractType !== selectedContractType) {
          return false;
        }

        // Feedback Status Filter
        const hasMgr = Boolean(stripHtml(item.managerFeedback || '').trim());
        const hasHr = Boolean(stripHtml(item.hrFeedback || '').trim());
        const hasAcd = Boolean(stripHtml(item.academyFeedback || '').trim());

        if (selectedFeedbackStatus === 'FULLY_REVIEWED' && !(hasMgr && hasHr && hasAcd)) {
          return false;
        }
        if (selectedFeedbackStatus === 'PARTIALLY_REVIEWED' && (!(hasMgr || hasHr || hasAcd) || (hasMgr && hasHr && hasAcd))) {
          return false;
        }
        if (selectedFeedbackStatus === 'PENDING_REVIEW' && (hasMgr || hasHr || hasAcd)) {
          return false;
        }

        // Date Range Filter (Contract Start Date)
        const sDate = parseDate(item.contractStartDate);
        if (startDate && sDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (sDate < start) return false;
        }
        if (endDate && sDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (sDate > end) return false;
        }

        // Search Term Filter
        if (searchTerm.trim()) {
          const query = searchTerm.toLowerCase().trim();
          const name = (item.employeeName || '').toLowerCase();
          const empId = (item.employeeId || '').toLowerCase();
          const dept = (item.department || '').toLowerCase();
          const cType = (item.contractType || '').toLowerCase();
          const pos = (item.positions || []).join(' ').toLowerCase();
          const proj = (item.projects || []).join(' ').toLowerCase();
          const mgrFb = stripHtml(item.managerFeedback || '').toLowerCase();
          const hrFb = stripHtml(item.hrFeedback || '').toLowerCase();
          const acdFb = stripHtml(item.academyFeedback || '').toLowerCase();

          const matches =
            name.includes(query) ||
            empId.includes(query) ||
            dept.includes(query) ||
            cType.includes(query) ||
            pos.includes(query) ||
            proj.includes(query) ||
            mgrFb.includes(query) ||
            hrFb.includes(query) ||
            acdFb.includes(query);

          if (!matches) return false;
        }

        return true;
      })
      .sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === 'contractStartDate' || sortField === 'contractEndDate' || sortField === 'createdAt') {
          valA = parseDate(valA) ? parseDate(valA).getTime() : 0;
          valB = parseDate(valB) ? parseDate(valB).getTime() : 0;
        } else if (Array.isArray(valA)) {
          valA = valA.join(', ').toLowerCase();
          valB = (valB || []).join(', ').toLowerCase();
        } else {
          valA = stripHtml((valA || '').toString()).toLowerCase();
          valB = stripHtml((valB || '').toString()).toLowerCase();
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
  }, [reviews, selectedDepartment, selectedContractType, selectedFeedbackStatus, startDate, endDate, searchTerm, sortField, sortOrder]);

  // Paginated Data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Sort Handler
  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('ALL');
    setSelectedContractType('ALL');
    setSelectedFeedbackStatus('ALL');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  // Feedback Editing Handlers
  const handleOpenSingleEdit = (review, fieldKey, fieldLabel) => {
    setEditModal({
      open: true,
      review,
      activeTab: fieldKey,
      managerFeedback: review.managerFeedback || '',
      hrFeedback: review.hrFeedback || '',
      academyFeedback: review.academyFeedback || '',
      singleFieldMode: true,
      fieldLabel
    });
  };

  const handleOpenFullEdit = (review) => {
    setEditModal({
      open: true,
      review,
      activeTab: 'managerFeedback',
      managerFeedback: review.managerFeedback || '',
      hrFeedback: review.hrFeedback || '',
      academyFeedback: review.academyFeedback || '',
      singleFieldMode: false,
      fieldLabel: 'Full Evaluation Feedback'
    });
  };

  const handleCloseEdit = () => {
    setEditModal((prev) => ({ ...prev, open: false }));
  };

  const handleSaveFeedback = async () => {
    if (!editModal.review?.id) return;
    setSavingFeedback(true);
    try {
      const payload = {
        managerFeedback: editModal.managerFeedback,
        hrFeedback: editModal.hrFeedback,
        academyFeedback: editModal.academyFeedback
      };

      const updated = await updateProbationReview(editModal.review.id, payload);

      // Update state in reviews
      setReviews((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
      );

      // Also update selectedReview if open
      if (selectedReview && selectedReview.id === updated.id) {
        setSelectedReview((prev) => ({ ...prev, ...updated }));
      }

      setToast({
        open: true,
        message: `Feedback for ${editModal.review.employeeName} saved successfully!`,
        severity: 'success'
      });
      handleCloseEdit();
    } catch (err) {
      console.error('Error updating feedback:', err);
      setToast({
        open: true,
        message: `Failed to save feedback: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleOpenViewHtml = ({ title, content, icon, iconColor }) => {
    setHtmlDialog({
      open: true,
      title,
      content,
      icon,
      iconColor: iconColor || THEME.accent
    });
  };

  const handleCloseHtmlDialog = () => {
    setHtmlDialog((prev) => ({ ...prev, open: false }));
  };

  // KPI Calculations
  const totalReviewsCount = reviews.length;
  const fullyReviewedCount = useMemo(() => {
    return reviews.filter(
      (r) =>
        Boolean(stripHtml(r.managerFeedback || '').trim()) &&
        Boolean(stripHtml(r.hrFeedback || '').trim()) &&
        Boolean(stripHtml(r.academyFeedback || '').trim())
    ).length;
  }, [reviews]);

  const partiallyReviewedCount = useMemo(() => {
    return reviews.filter((r) => {
      const hasMgr = Boolean(stripHtml(r.managerFeedback || '').trim());
      const hasHr = Boolean(stripHtml(r.hrFeedback || '').trim());
      const hasAcd = Boolean(stripHtml(r.academyFeedback || '').trim());
      return (hasMgr || hasHr || hasAcd) && !(hasMgr && hasHr && hasAcd);
    }).length;
  }, [reviews]);

  const pendingReviewsCount = useMemo(() => {
    return reviews.filter((r) => {
      const hasMgr = Boolean(stripHtml(r.managerFeedback || '').trim());
      const hasHr = Boolean(stripHtml(r.hrFeedback || '').trim());
      const hasAcd = Boolean(stripHtml(r.academyFeedback || '').trim());
      return !hasMgr && !hasHr && !hasAcd;
    }).length;
  }, [reviews]);

  // Export to Excel (.xlsx)
  const handleExportExcel = () => {
    const rows = filteredData.map((item) => {
      const positionsStr = (item.positions || []).join(', ');
      const projectsStr = (item.projects || []).join(', ');

      return {
        'Photo': `${IMAGE_BASE_URL}${item.employeeId}`,
        'Employee Name': item.employeeName || 'N/A',
        'Department': item.department || 'N/A',
        'Positions': positionsStr || 'None',
        'Projects': projectsStr || 'None',
        'Contract Type': item.contractType || 'N/A',
        'Contract Status': item.contractStatus || 'N/A',
        'Start Date': formatDisplayDate(item.contractStartDate),
        'End Date': formatDisplayDate(item.contractEndDate),
        'Manager Feedback': stripHtml(item.managerFeedback) || 'None',
        'HR Feedback': stripHtml(item.hrFeedback) || 'None',
        'Academy Feedback': stripHtml(item.academyFeedback) || 'None'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!worksheet[address]) continue;
      worksheet[address].s = {
        fill: { fgColor: { rgb: '1E293B' } },
        font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 11 },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'medium', color: { rgb: '3B82F6' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };
    }

    worksheet['!cols'] = [
      { wch: 30 }, // Photo
      { wch: 24 }, // Name
      { wch: 18 }, // Department
      { wch: 22 }, // Positions
      { wch: 16 }, // Projects
      { wch: 14 }, // Contract Type
      { wch: 14 }, // Status
      { wch: 14 }, // Start Date
      { wch: 14 }, // End Date
      { wch: 36 }, // Manager FB
      { wch: 36 }, // HR FB
      { wch: 36 }  // Academy FB
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Probation Reviews');
    const filename = `Probation_Reviews_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // Export to PDF (.pdf) with Circular Avatars & Branding Banner
  const handleExportPDF = async () => {
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;

      const axoLogoUrl = '/cLogo.png';
      const [axoBase64, marabesBase64] = await Promise.all([
        getBase64FromUrl(axoLogoUrl).then((res) => res || getBase64FromUrl(axopeopleLogo)),
        getBase64FromUrl(marabesLogo)
      ]);

      // Pre-fetch all candidate/employee photos in base64 cropped as circles
      const imageBase64Map = {};
      await Promise.all(
        filteredData.map(async (item) => {
          const key = item.employeeId || item.id || item.employeeName;
          try {
            let blobUrl = cachedImages[item.employeeId];
            if (!blobUrl && item.employeeId) {
              blobUrl = await getEmployeeImage(item.employeeId);
            }
            if (blobUrl) {
              const b64 = await getBase64FromUrl(blobUrl);
              if (b64) {
                imageBase64Map[key] = await makeCircularImageBase64(b64);
                return;
              }
            }
          } catch (e) {
            // Ignore
          }
          imageBase64Map[key] = createInitialsAvatarBase64(item.employeeName);
        })
      );

      // PDF HEADER WITH LOGOS
      const addHeader = (pageNum) => {
        if (pageNum > 1) doc.addPage();

        // Banner box
        doc.setFillColor(243, 244, 246);
        doc.rect(0, 0, pageWidth, 22, 'F');

        // Left Logo: Marabes
        try {
          if (marabesBase64) {
            doc.addImage(marabesBase64, 'PNG', margin, 5, 28, 12);
          }
        } catch (e) { }

        // Right Logo: AXO
        try {
          if (axoBase64) {
            doc.addImage(axoBase64, 'PNG', pageWidth - margin - 26, 5, 26, 12);
          }
        } catch (e) { }

        // Center Title
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 44, 89);
        doc.text('PROBATION REVIEWS & EVALUATION REPORT', pageWidth / 2, 14, { align: 'center' });
      };

      addHeader(1);

      const tableColumns = [
        { header: 'Photo', dataKey: 'photo' },
        { header: 'Employee', dataKey: 'employee' },
        { header: 'Dept & Position', dataKey: 'deptPos' },
        { header: 'Contract', dataKey: 'contract' },
        { header: 'Manager Feedback', dataKey: 'managerFeedback' },
        { header: 'HR Feedback', dataKey: 'hrFeedback' },
        { header: 'Academy Feedback', dataKey: 'academyFeedback' }
      ];

      const tableData = filteredData.map((item) => {
        const posStr = (item.positions || []).join(', ');
        const projStr = (item.projects || []).length > 0 ? ` [${item.projects.join(', ')}]` : '';

        return {
          _rawItem: item,
          photo: '',
          employee: item.employeeName || 'N/A',
          deptPos: `${item.department || 'N/A'}\n${posStr || 'Staff'}${projStr}`,
          contract: `${item.contractType || 'N/A'} (${item.contractStatus || 'ACTIVE'})\n${formatDisplayDate(item.contractStartDate)} - ${formatDisplayDate(item.contractEndDate)}`,
          managerFeedback: stripHtml(item.managerFeedback) || 'None',
          hrFeedback: stripHtml(item.hrFeedback) || 'None',
          academyFeedback: stripHtml(item.academyFeedback) || 'None'
        };
      });

      autoTable(doc, {
        startY: 28,
        columns: tableColumns,
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 3.5,
          valign: 'middle',
          overflow: 'linebreak',
          minCellHeight: 14
        },
        headStyles: {
          fillColor: [30, 41, 59],
          textColor: 255,
          fontSize: 8.5,
          fontStyle: 'bold',
          halign: 'left'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 16, halign: 'center' },
          1: { cellWidth: 34, fontStyle: 'bold' },
          2: { cellWidth: 38 },
          3: { cellWidth: 36 },
          4: { cellWidth: 50 },
          5: { cellWidth: 50 },
          6: { cellWidth: 50 }
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.dataKey === 'photo') {
            const raw = data.row.raw?._rawItem || filteredData[data.row.index];
            if (raw) {
              const key = raw.employeeId || raw.id || raw.employeeName;
              const b64 = imageBase64Map[key];
              if (b64) {
                try {
                  const imgSize = 10;
                  const posX = data.cell.x + (data.cell.width - imgSize) / 2;
                  const posY = data.cell.y + (data.cell.height - imgSize) / 2;
                  doc.addImage(b64, 'PNG', posX, posY, imgSize, imgSize);
                } catch (e) {
                  console.warn('Error rendering photo in PDF:', e);
                }
              }
            }
          }
        },
        didDrawPage: (data) => {
          const str = `Page ${doc.internal.getNumberOfPages()} | Total: ${filteredData.length} records`;
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(str, pageWidth - 60, pageHeight - 8);
        }
      });

      doc.save(`Probation_Reviews_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      alert('Failed to generate PDF: ' + err.message);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={styles.container}>
        {/* === MAIN HEADER TOOLBAR CARD === */}
        <Box sx={styles.headerCard}>
          <Box>
            <Typography sx={styles.title}>
              <ReviewIcon sx={{ color: THEME.accent, fontSize: '1.75rem' }} />
              Probation Reviews Management
            </Typography>
            <Typography sx={styles.subTitle}>
              Track, evaluate, and edit Manager, HR, and Academy feedback for new starter probation contracts
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {/* View Mode Switcher */}
            <Tooltip title="Table View">
              <IconButton
                size="small"
                onClick={() => setViewMode('table')}
                sx={{
                  border: `1px solid ${THEME.border}`,
                  backgroundColor: viewMode === 'table' ? THEME.accentLight : 'transparent',
                  color: viewMode === 'table' ? THEME.accent : THEME.secondary,
                  borderRadius: '8px',
                  p: 1
                }}
              >
                <ViewListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Card Grid View">
              <IconButton
                size="small"
                onClick={() => setViewMode('grid')}
                sx={{
                  border: `1px solid ${THEME.border}`,
                  backgroundColor: viewMode === 'grid' ? THEME.accentLight : 'transparent',
                  color: viewMode === 'grid' ? THEME.accent : THEME.secondary,
                  borderRadius: '8px',
                  p: 1
                }}
              >
                <GridViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Sync / Backfill Eligible Contracts */}
            <RestrictedToRole allowedRoles={['DEV']}>
              <Tooltip title="Sync & initialize probation reviews for eligible new contracts">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  onClick={handleBackfill}
                  disabled={loading || syncing}
                  sx={{
                    borderColor: THEME.purple,
                    color: THEME.purple,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.75,
                    '&:hover': {
                      borderColor: '#7C3AED',
                      backgroundColor: THEME.purpleLight
                    }
                  }}
                >
                  {syncing ? 'Syncing...' : 'Sync Contracts'}
                </Button>
              </Tooltip>
            </RestrictedToRole>

            {/* Refresh Button */}
            <Tooltip title="Refresh Data">
              <IconButton
                size="small"
                onClick={fetchData}
                disabled={loading}
                sx={{
                  border: `1px solid ${THEME.border}`,
                  color: THEME.secondary,
                  borderRadius: '8px',
                  p: 1,
                  '&:hover': { color: THEME.accent, backgroundColor: THEME.accentLight }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Export to Excel */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExcelIcon />}
              onClick={handleExportExcel}
              disabled={loading || filteredData.length === 0}
              sx={{
                borderColor: '#10B981',
                color: '#10B981',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 1.5,
                py: 0.75,
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#ECFDF5'
                }
              }}
            >
              Export Excel
            </Button>

            {/* Export to PDF */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              onClick={handleExportPDF}
              disabled={loading || filteredData.length === 0}
              sx={{
                borderColor: '#EF4444',
                color: '#EF4444',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 1.5,
                py: 0.75,
                '&:hover': {
                  borderColor: '#DC2626',
                  backgroundColor: '#FEF2F2'
                }
              }}
            >
              Export PDF
            </Button>
          </Stack>
        </Box>

        {/* === METRICS / STATS CARDS === */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.accentLight, THEME.accent)}>
                <PersonIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Total Reviews
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.primary }}>
                  {totalReviewsCount}{' '}
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                    eligible
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.successLight, THEME.success)}>
                <CheckCircleIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Fully Completed
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.success }}>
                  {fullyReviewedCount}{' '}
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                    ({totalReviewsCount > 0 ? Math.round((fullyReviewedCount / totalReviewsCount) * 100) : 0}%)
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.warningLight, THEME.warning)}>
                <ReviewIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Partially / Pending
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.warning }}>
                  {partiallyReviewedCount}{' '}
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: THEME.rose, fontWeight: 600 }}>
                    ({pendingReviewsCount} pending)
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.purpleLight, THEME.purple)}>
                <BusinessIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Departments Involved
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.primary }}>
                  {departments.length}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* === FILTER CONTROLS CARD === */}
        <Box sx={styles.filterCard}>
          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search employee, role, project, feedback..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: THEME.secondary, fontSize: '1.1rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: '#FFFFFF',
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' }
                }}
              />
            </Grid>

            {/* Department Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="dept-select-label">Department</InputLabel>
                <Select
                  labelId="dept-select-label"
                  value={selectedDepartment}
                  label="Department"
                  onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF', fontSize: '0.875rem' }}
                >
                  <MenuItem value="ALL">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Contract Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="contract-type-select-label">Contract Type</InputLabel>
                <Select
                  labelId="contract-type-select-label"
                  value={selectedContractType}
                  label="Contract Type"
                  onChange={(e) => {
                    setSelectedContractType(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF', fontSize: '0.875rem' }}
                >
                  <MenuItem value="ALL">All Contract Types</MenuItem>
                  {contractTypes.map((ct) => (
                    <MenuItem key={ct} value={ct}>
                      {ct}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Feedback Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="fb-status-select-label">Feedback Status</InputLabel>
                <Select
                  labelId="fb-status-select-label"
                  value={selectedFeedbackStatus}
                  label="Feedback Status"
                  onChange={(e) => {
                    setSelectedFeedbackStatus(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF', fontSize: '0.875rem' }}
                >
                  <MenuItem value="ALL">All Statuses ({reviews.length})</MenuItem>
                  <MenuItem value="FULLY_REVIEWED">Fully Reviewed ({fullyReviewedCount})</MenuItem>
                  <MenuItem value="PARTIALLY_REVIEWED">Partially Reviewed ({partiallyReviewedCount})</MenuItem>
                  <MenuItem value="PENDING_REVIEW">Pending All Reviews ({pendingReviewsCount})</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Start Date Range (From) */}
            <Grid item xs={12} sm={6} md={1.5}>
              <DatePicker
                label="Start Date (From)"
                value={startDate}
                onChange={(newVal) => {
                  setStartDate(newVal);
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      '& .MuiInputBase-root': { borderRadius: '10px' }
                    }
                  }
                }}
              />
            </Grid>

            {/* Start Date Range (To) */}
            <Grid item xs={12} sm={6} md={1.5}>
              <DatePicker
                label="Start Date (To)"
                value={endDate}
                onChange={(newVal) => {
                  setEndDate(newVal);
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      '& .MuiInputBase-root': { borderRadius: '10px' }
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ERROR STATE */}
        {error && (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* LOADING STATE */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: THEME.accent }} />
            <Typography sx={{ mt: 2, color: THEME.secondary, fontSize: '0.9rem' }}>
              Loading probation reviews...
            </Typography>
          </Box>
        ) : filteredData.length === 0 ? (
          /* EMPTY STATE */
          <Paper sx={{ ...styles.tableCard, py: 8, textAlign: 'center', px: 2 }}>
            <InfoIcon sx={{ fontSize: '3rem', color: THEME.secondary, mb: 1.5 }} />
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: THEME.primary }}>
              No probation reviews found
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: THEME.secondary, mt: 0.5, maxWidth: 450, mx: 'auto' }}>
              No records match your filter criteria. Try adjusting your filters or click "Sync Contracts" to initialize probation reviews.
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SyncIcon />}
                onClick={handleBackfill}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: THEME.primary }}
              >
                Sync Contracts
              </Button>
            </Stack>
          </Paper>
        ) : viewMode === 'table' ? (
          /* TABLE VIEW */
          <Box sx={styles.tableCard}>
            <TableContainer>
              <Table sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...styles.th, width: 70, textAlign: 'center' }}>Photo</TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 180 }}>
                      <TableSortLabel
                        active={sortField === 'employeeName'}
                        direction={sortField === 'employeeName' ? sortOrder : 'asc'}
                        onClick={() => handleSort('employeeName')}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 150 }}>
                      <TableSortLabel
                        active={sortField === 'department'}
                        direction={sortField === 'department' ? sortOrder : 'asc'}
                        onClick={() => handleSort('department')}
                      >
                        Dept & Projects
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 160 }}>
                      <TableSortLabel
                        active={sortField === 'contractStartDate'}
                        direction={sortField === 'contractStartDate' ? sortOrder : 'asc'}
                        onClick={() => handleSort('contractStartDate')}
                      >
                        Contract Timeline
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 220 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ManagerIcon sx={{ fontSize: '1rem', color: THEME.accent }} />
                        <span>Manager Feedback</span>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 220 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <HrIcon sx={{ fontSize: '1rem', color: THEME.purple }} />
                        <span>HR Feedback</span>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 220 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AcademyIcon sx={{ fontSize: '1rem', color: THEME.teal }} />
                        <span>Academy Feedback</span>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, textAlign: 'center', width: 90 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.map((review) => {
                    const positions = review.positions || [];
                    const projects = review.projects || [];

                    return (
                      <TableRow
                        key={review.id || review.contractId}
                        hover
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        {/* Circular Photo */}
                        <TableCell sx={{ ...styles.td, textAlign: 'center' }}>
                          <EmployeeAvatar
                            employeeId={review.employeeId}
                            name={review.employeeName}
                            size={42}
                            fontSize="0.9rem"
                            cachedImages={cachedImages}
                          />
                        </TableCell>

                        {/* Employee Name & Positions */}
                        <TableCell sx={styles.td}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: THEME.primary }}>
                            {review.employeeName || 'Unnamed Employee'}
                          </Typography>
                          {positions.length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {positions.map((pos, idx) => (
                                <Chip
                                  key={idx}
                                  label={pos}
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    backgroundColor: THEME.accentLight,
                                    color: THEME.accentDark
                                  }}
                                />
                              ))}
                            </Stack>
                          )}
                        </TableCell>

                        {/* Department & Projects */}
                        <TableCell sx={styles.td}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1E293B' }}>
                            {review.department || 'N/A'}
                          </Typography>
                          {projects.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {projects.map((proj, idx) => (
                                <Chip
                                  key={idx}
                                  icon={<ProjectIcon sx={{ fontSize: '0.75rem !important' }} />}
                                  label={proj}
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    backgroundColor: '#F1F5F9',
                                    color: '#475569'
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: THEME.secondary, mt: 0.3 }}>
                              No project assigned
                            </Typography>
                          )}
                        </TableCell>

                        {/* Contract Type, Status & Dates */}
                        <TableCell sx={styles.td}>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                            <Chip
                              label={review.contractType || 'N/A'}
                              size="small"
                              sx={{
                                height: '22px',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                backgroundColor: THEME.purpleLight,
                                color: THEME.purple
                              }}
                            />
                            <Chip
                              label={review.contractStatus || 'ACTIVE'}
                              size="small"
                              sx={{
                                height: '22px',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                backgroundColor: review.contractStatus === 'ACTIVE' ? THEME.successLight : '#F1F5F9',
                                color: review.contractStatus === 'ACTIVE' ? THEME.success : THEME.secondary
                              }}
                            />
                          </Stack>
                          <Typography sx={{ fontSize: '0.75rem', color: THEME.secondary }}>
                            {formatDisplayDate(review.contractStartDate)} &rarr; {formatDisplayDate(review.contractEndDate)}
                          </Typography>
                        </TableCell>

                        {/* Editable Manager Feedback */}
                        <TableCell sx={styles.td}>
                          <EditableFeedbackCell
                            value={review.managerFeedback}
                            fieldKey="managerFeedback"
                            fieldLabel="Manager Feedback"
                            review={review}
                            icon={ManagerIcon}
                            iconColor={THEME.accent}
                            onOpenEdit={handleOpenSingleEdit}
                            onOpenView={handleOpenViewHtml}
                          />
                        </TableCell>

                        {/* Editable HR Feedback */}
                        <TableCell sx={styles.td}>
                          <EditableFeedbackCell
                            value={review.hrFeedback}
                            fieldKey="hrFeedback"
                            fieldLabel="HR Feedback"
                            review={review}
                            icon={HrIcon}
                            iconColor={THEME.purple}
                            onOpenEdit={handleOpenSingleEdit}
                            onOpenView={handleOpenViewHtml}
                          />
                        </TableCell>

                        {/* Editable Academy Feedback */}
                        <TableCell sx={styles.td}>
                          <EditableFeedbackCell
                            value={review.academyFeedback}
                            fieldKey="academyFeedback"
                            fieldLabel="Academy Feedback"
                            review={review}
                            icon={AcademyIcon}
                            iconColor={THEME.teal}
                            onOpenEdit={handleOpenSingleEdit}
                            onOpenView={handleOpenViewHtml}
                          />
                        </TableCell>

                        {/* Actions: Edit All & View Full Dossier */}
                        <TableCell sx={{ ...styles.td, textAlign: 'center' }}>
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Edit All Feedbacks">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenFullEdit(review)}
                                sx={{
                                  color: THEME.accent,
                                  backgroundColor: THEME.accentLight,
                                  '&:hover': { backgroundColor: '#DBEAFE' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Full Review Dossier">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedReview(review);
                                  setDossierOpen(true);
                                }}
                                sx={{
                                  color: THEME.secondary,
                                  '&:hover': { color: THEME.primary, backgroundColor: '#F1F5F9' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{
                borderTop: `1px solid ${THEME.border}`,
                backgroundColor: '#FFFFFF',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.85rem',
                  color: THEME.secondary
                }
              }}
            />
          </Box>
        ) : (
          /* CARD GRID VIEW */
          <Box>
            <Grid container spacing={2.5}>
              {paginatedData.map((review) => {
                const positions = review.positions || [];
                const projects = review.projects || [];

                return (
                  <Grid item xs={12} sm={6} md={4} key={review.id || review.contractId}>
                    <Card sx={styles.reviewCard}>
                      <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Header Profile & Role */}
                        <Stack direction="row" spacing={2} alignItems="center">
                          <EmployeeAvatar
                            employeeId={review.employeeId}
                            name={review.employeeName}
                            size={52}
                            fontSize="1.1rem"
                            cachedImages={cachedImages}
                          />
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: THEME.primary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {review.employeeName || 'Unnamed Employee'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.825rem', color: THEME.secondary }}>
                              {review.department || 'N/A'}
                            </Typography>
                          </Box>
                          <Tooltip title="Edit All Feedbacks">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenFullEdit(review)}
                              sx={{ color: THEME.accent, backgroundColor: THEME.accentLight }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Divider />

                        {/* Badges: Contract & Projects */}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            label={review.contractType || 'ANAPEC'}
                            size="small"
                            sx={{ fontWeight: 700, bgcolor: THEME.purpleLight, color: THEME.purple, height: '22px' }}
                          />
                          <Chip
                            label={review.contractStatus || 'ACTIVE'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: review.contractStatus === 'ACTIVE' ? THEME.successLight : '#F1F5F9',
                              color: review.contractStatus === 'ACTIVE' ? THEME.success : THEME.secondary,
                              height: '22px'
                            }}
                          />
                          {projects.map((p, i) => (
                            <Chip
                              key={i}
                              icon={<ProjectIcon sx={{ fontSize: '0.75rem !important' }} />}
                              label={p}
                              size="small"
                              sx={{ height: '22px', fontSize: '0.7rem', bgcolor: '#F1F5F9' }}
                            />
                          ))}
                        </Stack>

                        {/* Feedbacks Grid */}
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: THEME.accent, mb: 0.5 }}>
                              MANAGER FEEDBACK
                            </Typography>
                            <EditableFeedbackCell
                              value={review.managerFeedback}
                              fieldKey="managerFeedback"
                              fieldLabel="Manager Feedback"
                              review={review}
                              icon={ManagerIcon}
                              iconColor={THEME.accent}
                              onOpenEdit={handleOpenSingleEdit}
                              onOpenView={handleOpenViewHtml}
                            />
                          </Box>

                          <Box>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: THEME.purple, mb: 0.5 }}>
                              HR FEEDBACK
                            </Typography>
                            <EditableFeedbackCell
                              value={review.hrFeedback}
                              fieldKey="hrFeedback"
                              fieldLabel="HR Feedback"
                              review={review}
                              icon={HrIcon}
                              iconColor={THEME.purple}
                              onOpenEdit={handleOpenSingleEdit}
                              onOpenView={handleOpenViewHtml}
                            />
                          </Box>

                          <Box>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: THEME.teal, mb: 0.5 }}>
                              ACADEMY FEEDBACK
                            </Typography>
                            <EditableFeedbackCell
                              value={review.academyFeedback}
                              fieldKey="academyFeedback"
                              fieldLabel="Academy Feedback"
                              review={review}
                              icon={AcademyIcon}
                              iconColor={THEME.teal}
                              onOpenEdit={handleOpenSingleEdit}
                              onOpenView={handleOpenViewHtml}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination for Grid */}
            <Box sx={{ mt: 3, ...styles.tableCard }}>
              <TablePagination
                rowsPerPageOptions={[6, 12, 24, 48]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontSize: '0.85rem',
                    color: THEME.secondary
                  }
                }}
              />
            </Box>
          </Box>
        )}

        {/* === RICH TEXT FEEDBACK EDIT DIALOG (MODAL) === */}
        <Dialog
          open={editModal.open}
          onClose={handleCloseEdit}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              backgroundColor: THEME.tableHeader,
              borderBottom: `1px solid ${THEME.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
              px: 3
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={styles.iconBox(THEME.accentLight, THEME.accent)}>
                <EditIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: THEME.primary }}>
                  {editModal.singleFieldMode ? `Edit ${editModal.fieldLabel}` : 'Probation Evaluation Feedbacks'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary }}>
                  {editModal.review?.employeeName} &bull; {editModal.review?.department}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleCloseEdit} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {!editModal.singleFieldMode ? (
              <Box>
                <Tabs
                  value={editModal.activeTab}
                  onChange={(e, val) => setEditModal((prev) => ({ ...prev, activeTab: val }))}
                  sx={{
                    borderBottom: `1px solid ${THEME.border}`,
                    mb: 2.5,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.875rem' }
                  }}
                >
                  <Tab
                    icon={<ManagerIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Manager Feedback"
                    value="managerFeedback"
                  />
                  <Tab
                    icon={<HrIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="HR Feedback"
                    value="hrFeedback"
                  />
                  <Tab
                    icon={<AcademyIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Academy Feedback"
                    value="academyFeedback"
                  />
                </Tabs>

                {editModal.activeTab === 'managerFeedback' && (
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: THEME.secondary, mb: 1 }}>
                      Evaluation and notes from the line manager / team lead:
                    </Typography>
                    <EditorConvertToHTML
                      value={editModal.managerFeedback}
                      onChange={(html) => setEditModal((prev) => ({ ...prev, managerFeedback: html }))}
                    />
                  </Box>
                )}

                {editModal.activeTab === 'hrFeedback' && (
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: THEME.secondary, mb: 1 }}>
                      Evaluation and notes from Human Resources:
                    </Typography>
                    <EditorConvertToHTML
                      value={editModal.hrFeedback}
                      onChange={(html) => setEditModal((prev) => ({ ...prev, hrFeedback: html }))}
                    />
                  </Box>
                )}

                {editModal.activeTab === 'academyFeedback' && (
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: THEME.secondary, mb: 1 }}>
                      Evaluation and notes from the Academy / Training department:
                    </Typography>
                    <EditorConvertToHTML
                      value={editModal.academyFeedback}
                      onChange={(html) => setEditModal((prev) => ({ ...prev, academyFeedback: html }))}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: THEME.secondary, mb: 1.5 }}>
                  Write or update rich formatted content for {editModal.fieldLabel}:
                </Typography>
                <EditorConvertToHTML
                  value={editModal[editModal.activeTab]}
                  onChange={(html) => setEditModal((prev) => ({ ...prev, [editModal.activeTab]: html }))}
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2.5, px: 3, borderTop: `1px solid ${THEME.border}`, backgroundColor: '#F8FAFC' }}>
            <Button onClick={handleCloseEdit} sx={{ textTransform: 'none', color: THEME.secondary, fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveFeedback}
              disabled={savingFeedback}
              startIcon={savingFeedback ? <CircularProgress size={16} color="inherit" /> : <AssignmentTurnedInIcon />}
              sx={{
                bgcolor: THEME.accent,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                borderRadius: '8px',
                '&:hover': { bgcolor: THEME.accentDark }
              }}
            >
              {savingFeedback ? 'Saving...' : 'Save Feedback'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* === FULL REVIEW DOSSIER DIALOG === */}
        {selectedReview && (
          <Dialog
            open={dossierOpen}
            onClose={() => setDossierOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
          >
            <DialogTitle
              sx={{
                backgroundColor: THEME.tableHeader,
                borderBottom: `1px solid ${THEME.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                px: 3
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmployeeAvatar
                  employeeId={selectedReview.employeeId}
                  name={selectedReview.employeeName}
                  size={46}
                  fontSize="1rem"
                  cachedImages={cachedImages}
                />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: THEME.primary }}>
                    {selectedReview.employeeName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary }}>
                    Probation Review Dossier &bull; {selectedReview.department}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={() => setDossierOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {/* Contract Information Summary */}
              <Box sx={{ p: 2, bgcolor: THEME.accentLight, borderRadius: '12px', mb: 3, border: `1px solid #BFDBFE` }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontSize: '0.75rem', color: THEME.secondary, fontWeight: 600 }}>Contract Type</Typography>
                    <Typography sx={{ fontWeight: 700, color: THEME.primary }}>{selectedReview.contractType || 'ANAPEC'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontSize: '0.75rem', color: THEME.secondary, fontWeight: 600 }}>Status</Typography>
                    <Chip
                      label={selectedReview.contractStatus || 'ACTIVE'}
                      size="small"
                      sx={{ height: '20px', fontWeight: 700, bgcolor: THEME.successLight, color: THEME.success }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontSize: '0.75rem', color: THEME.secondary, fontWeight: 600 }}>Start Date</Typography>
                    <Typography sx={{ fontWeight: 700, color: THEME.primary }}>{formatDisplayDate(selectedReview.contractStartDate)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontSize: '0.75rem', color: THEME.secondary, fontWeight: 600 }}>End Date</Typography>
                    <Typography sx={{ fontWeight: 700, color: THEME.primary }}>{formatDisplayDate(selectedReview.contractEndDate)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* All 3 Feedbacks */}
              <Stack spacing={2.5}>
                {/* Manager */}
                <Card sx={{ border: `1px solid ${THEME.border}`, borderRadius: '12px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ManagerIcon sx={{ color: THEME.accent }} />
                        <Typography sx={{ fontWeight: 700, color: THEME.primary, fontSize: '0.95rem' }}>
                          Manager Feedback
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenSingleEdit(selectedReview, 'managerFeedback', 'Manager Feedback')}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Edit
                      </Button>
                    </Stack>
                    {selectedReview.managerFeedback && stripHtml(selectedReview.managerFeedback).trim() ? (
                      <Box sx={styles.richHtmlContent} dangerouslySetInnerHTML={{ __html: selectedReview.managerFeedback }} />
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: THEME.secondary, fontSize: '0.85rem' }}>
                        No manager feedback recorded yet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* HR */}
                <Card sx={{ border: `1px solid ${THEME.border}`, borderRadius: '12px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HrIcon sx={{ color: THEME.purple }} />
                        <Typography sx={{ fontWeight: 700, color: THEME.primary, fontSize: '0.95rem' }}>
                          HR Feedback
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenSingleEdit(selectedReview, 'hrFeedback', 'HR Feedback')}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Edit
                      </Button>
                    </Stack>
                    {selectedReview.hrFeedback && stripHtml(selectedReview.hrFeedback).trim() ? (
                      <Box sx={styles.richHtmlContent} dangerouslySetInnerHTML={{ __html: selectedReview.hrFeedback }} />
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: THEME.secondary, fontSize: '0.85rem' }}>
                        No HR feedback recorded yet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Academy */}
                <Card sx={{ border: `1px solid ${THEME.border}`, borderRadius: '12px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AcademyIcon sx={{ color: THEME.teal }} />
                        <Typography sx={{ fontWeight: 700, color: THEME.primary, fontSize: '0.95rem' }}>
                          Academy Feedback
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenSingleEdit(selectedReview, 'academyFeedback', 'Academy Feedback')}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Edit
                      </Button>
                    </Stack>
                    {selectedReview.academyFeedback && stripHtml(selectedReview.academyFeedback).trim() ? (
                      <Box sx={styles.richHtmlContent} dangerouslySetInnerHTML={{ __html: selectedReview.academyFeedback }} />
                    ) : (
                      <Typography sx={{ fontStyle: 'italic', color: THEME.secondary, fontSize: '0.85rem' }}>
                        No academy feedback recorded yet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2, px: 3, borderTop: `1px solid ${THEME.border}`, bgcolor: '#F8FAFC' }}>
              <Button onClick={() => setDossierOpen(false)} sx={{ textTransform: 'none', color: THEME.secondary }}>
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setDossierOpen(false);
                  handleOpenFullEdit(selectedReview);
                }}
                sx={{ textTransform: 'none', bgcolor: THEME.primary }}
              >
                Edit All Feedbacks
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* === READ-ONLY HTML CONTENT VIEWER DIALOG === */}
        <Dialog
          open={htmlDialog.open}
          onClose={handleCloseHtmlDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              backgroundColor: THEME.tableHeader,
              borderBottom: `1px solid ${THEME.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
              px: 3
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {htmlDialog.icon && (
                <Box sx={styles.iconBox(THEME.accentLight, htmlDialog.iconColor)}>
                  <htmlDialog.icon />
                </Box>
              )}
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: THEME.primary }}>
                {htmlDialog.title}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseHtmlDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Box
              sx={styles.richHtmlContent}
              dangerouslySetInnerHTML={{
                __html: isHtml(htmlDialog.content)
                  ? htmlDialog.content
                  : `<p>${htmlDialog.content || 'None'}</p>`
              }}
            />
          </DialogContent>

          <DialogActions sx={{ p: 2, px: 3, borderTop: `1px solid ${THEME.border}`, bgcolor: '#F8FAFC' }}>
            <Button onClick={handleCloseHtmlDialog} variant="contained" sx={{ textTransform: 'none', bgcolor: THEME.primary }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* === NOTIFICATION TOAST === */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={toast.severity}
            onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            sx={{ borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default ProbationReviewReport;
